// src/pages/driver/cards/EmptyBinCard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  updateDoc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { db } from "../../../services/firebase";

/* ==============================
   Constants & helpers
   ============================== */
const MATERIALS = [
  "Plaster",
  "Timber",
  "Plastic",
  "Cardboard",
  "Particleboard",
  "MDF",
  "Glass",
  "Polystyrene",
  "Metal",
  "Rubble (Tiles)",
  "Rubble (Concrete)",
  "Insulation",
  "Carpet",
  "Ceiling Tiles",
  "Cable",
  "EPA Waste",
  "Tyres",
  "Metal ducting (rigid)",
  "Flexible ducting (insulated/flexi)",
  "Electrical fittings (e.g. lights)",
  "General waste",
];

const QUICK_STEPS = [25, 50, 75, 100];
const SILICA_MATERIAL_NAME = "Silica (by kg)";
const SILICA_WEIGHT_OPTIONS = Array.from(
  { length: (600 - 250) / 50 + 1 },
  (_, i) => 250 + i * 50,
);
const SILICA_UNIT_PRICE = 1;
const CHARGE_ITEMS = [
  {
    key: "chairs",
    name: "Chairs",
    label: "Chairs (per chair - $10 each)",
    unitLabel: "chair",
    unitPrice: 10,
  },
  {
    key: "pallets",
    name: "Pallets",
    label: "Pallets (per pallet - $10 each)",
    unitLabel: "pallet",
    unitPrice: 10,
  },
];

function normalizeText(v) {
  return String(v || "")
    .trim()
    .toLowerCase();
}

function isSilicaCategoryName(name) {
  return normalizeText(name).includes("silica");
}

function isSilicaMaterialName(name) {
  return normalizeText(name).includes("silica");
}

function fmtMoney(value) {
  const n = Number(value || 0);
  return `$${n.toFixed(2)}`;
}

// Robust fetch that works with different data shapes:
//  1) top-level with companyId
//  2) subcollection companies/{id}/{colName}
//  3) top-level unfiltered, then in-memory filter
async function fetchCompanyDocs(colName, companyId, companyName) {
  const out = [];

  try {
    const q1 = query(
      collection(db, colName),
      where("companyId", "==", companyId),
    );
    const s1 = await getDocs(q1);
    s1.forEach((d) => out.push({ id: d.id, ...d.data() }));
    if (out.length) return out;
  } catch (_) {}

  try {
    const s2 = await getDocs(
      collection(db, `companies/${companyId}/${colName}`),
    );
    s2.forEach((d) => out.push({ id: d.id, ...d.data() }));
    if (out.length) return out;
  } catch (_) {}

  try {
    const s3 = await getDocs(collection(db, colName));
    s3.forEach((d) => {
      const data = d.data();
      const match =
        data.companyId === companyId ||
        data.company?.id === companyId ||
        (companyName && data.companyName === companyName);
      if (match) out.push({ id: d.id, ...data });
    });
  } catch (_) {}

  return out;
}

/* ---------- CSV helpers ---------- */
function escCSV(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function rowsToCSV(rows) {
  return "\ufeff" + rows.map((r) => r.map(escCSV).join(",")).join("\r\n");
}
function downloadCSV(filename, rows) {
  const csv = rowsToCSV(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(a.href);
  a.remove();
}
function fmtDate(ms) {
  if (!ms) return "";
  return new Date(ms).toLocaleString("en-AU", { hour12: false });
}

/* ---------- Ledger writer (for invoicing) ---------- */
async function upsertLoadsLedger(db, { job, companyId, companyName, loads }) {
  if (!job?.id || !Array.isArray(loads) || !loads.length) return;
  const batch = writeBatch(db);

  loads.forEach((l, i) => {
    const createdAt = l.createdAt || Date.now() + i;
    const key = `${job.id}_${createdAt}`;
    const ref = doc(db, "loads", key);

    batch.set(
      ref,
      {
        _key: key,
        jobId: job.id,
        jobRef: doc(db, "jobs", job.id),
        jobTypeCode: job.jobTypeCode || "",
        jobTypeName: job.jobTypeName || "",
        companyId: companyId || null,
        companyName: companyName || job.companyName || "",
        customerId: job.customerId || null,
        customerName: job.customerName || "",
        driverId: job.driverId || null,
        driverEmail: job.driverEmail || "",
        serviceDate: job.scheduledAt || job.date || Date.now(),
        createdAt,
        updatedAt: serverTimestamp(),
        truckId: l.truckId || null,
        truckRego: l.truckRego || "",
        loadType: l.loadType || "",
        binId: l.binId || null,
        binName: l.binName || "",
        binCategoryId: l.binCategoryId || "",
        binCategoryName: l.binCategoryName || "",
        overload: !!l.overload,
        units: Number(l.units || 0),
        materials: Array.isArray(l.materials) ? l.materials : [],
        silicaWeightKg: Number(l.silicaWeightKg || 0),
        bagCount: Number(l.bagCount || 0),
        chargeType: l.chargeType || "",
        chargeQuantity: Number(l.chargeQuantity || 0),
        chargeUnitLabel: l.chargeUnitLabel || "",
        unitPrice:
          l.unitPrice === null || l.unitPrice === undefined
            ? null
            : Number(l.unitPrice || 0),
        amount:
          l.amount === null || l.amount === undefined
            ? null
            : Number(l.amount || 0),
        currency: "AUD",
        status: "unbilled",
        source: "emptyBinCard",
        loadPhotoUrl: l.photoUrl || "",
      },
      { merge: true },
    );
  });

  await batch.commit();
}

/* ==============================
   Component
   ============================== */
export default function EmptyBinCard({ job, company }) {
  const navigate = useNavigate();

  const companyId = job?.companyId || company?.id || null;
  const companyName = job?.companyName || company?.name || "";

  const [trucks, setTrucks] = useState([]);
  const [bins, setBins] = useState([]);
  const [categories, setCategories] = useState([]);

  const [truckId, setTruckId] = useState(job?.truckId || "");
  const [categoryId, setCategoryId] = useState(job?.binCategoryId || "");
  const [binId, setBinId] = useState(job?.binId || "");

  const [overload, setOverload] = useState(false);

  const [currentBin, setCurrentBin] = useState(() => ({}));
  const [loads, setLoads] = useState(
    Array.isArray(job?.emptyLoads) ? job.emptyLoads : [],
  );

  const [silicaWeightKg, setSilicaWeightKg] = useState("");
  const [silicaBagCount, setSilicaBagCount] = useState("");
  const [chargeItemCounts, setChargeItemCounts] = useState(() =>
    CHARGE_ITEMS.reduce((acc, item) => ({ ...acc, [item.key]: "" }), {}),
  );

  const [uploading, setUploading] = useState({});
  const [hasSaved, setHasSaved] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const storage = getStorage();

  useEffect(() => {
    let on = true;

    (async () => {
      if (!companyId) {
        setTrucks([]);
        setBins([]);
        setCategories([]);
        return;
      }

      const [t, b, c] = await Promise.all([
        fetchCompanyDocs("trucks", companyId, companyName),
        fetchCompanyDocs("bins", companyId, companyName),
        fetchCompanyDocs("bin_categories", companyId, companyName),
      ]);

      if (!on) return;

      t.sort((a, b) =>
        String(a.rego || a.name || "").localeCompare(
          String(b.rego || b.name || ""),
        ),
      );

      b.sort((a, b) =>
        String(a.name || a.size || "").localeCompare(
          String(b.name || b.size || ""),
        ),
      );

      const categoryMap = new Map();

      c.forEach((cat) => {
        categoryMap.set(cat.id, {
          id: cat.id,
          name: cat.name || "Untitled category",
        });
      });

      b.forEach((bin) => {
        if (bin.categoryId && !categoryMap.has(bin.categoryId)) {
          categoryMap.set(bin.categoryId, {
            id: bin.categoryId,
            name: bin.categoryName || "Untitled category",
          });
        }
      });

      const categoryList = Array.from(categoryMap.values()).sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || "")),
      );

      setTrucks(t);
      setBins(b);
      setCategories(categoryList);
    })();

    return () => {
      on = false;
    };
  }, [companyId, companyName]);

  const selectedTruck = useMemo(
    () => trucks.find((t) => t.id === truckId),
    [trucks, truckId],
  );

  const binsForCategory = useMemo(() => {
    if (!categoryId) return [];
    return bins.filter(
      (b) => String(b.categoryId || "") === String(categoryId),
    );
  }, [bins, categoryId]);

  const selectedBin = useMemo(
    () => bins.find((b) => b.id === binId),
    [bins, binId],
  );

  const selectedCategory = useMemo(() => {
    if (selectedBin?.categoryId) {
      return (
        categories.find((c) => c.id === selectedBin.categoryId) || {
          id: selectedBin.categoryId,
          name: selectedBin.categoryName || "",
        }
      );
    }
    return categories.find((c) => c.id === categoryId) || null;
  }, [categories, selectedBin, categoryId]);

  const selectedCategoryName =
    selectedBin?.categoryName || selectedCategory?.name || "";

  const selectedIsSilica = isSilicaCategoryName(selectedCategoryName);
  const unitsMultiplier = overload ? 1.5 : 1;

  useEffect(() => {
    if (!binId) return;
    const bin = bins.find((b) => b.id === binId);
    if (!bin) return;
    if (bin.categoryId && bin.categoryId !== categoryId) {
      setCategoryId(bin.categoryId);
    }
  }, [binId, bins, categoryId]);

  useEffect(() => {
    if (!categoryId) {
      if (binId) setBinId("");
      return;
    }

    const exists = bins.some(
      (b) =>
        b.id === binId && String(b.categoryId || "") === String(categoryId),
    );

    if (!exists) {
      setBinId("");
    }

    setCurrentBin({});
    setSilicaWeightKg("");
    setSilicaBagCount("");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally excludes binId: this only resets the load-entry form when the category changes, not on every bin pick within the same category (see conversation notes on this file)
  }, [categoryId, bins]);

  const totalUnits = useMemo(
    () => loads.reduce((sum, l) => sum + Number(l.units || 0), 0),
    [loads],
  );

  const totalsByMaterial = useMemo(() => {
    const acc = {};
    for (const l of loads) {
      const u = Number(l.units || 0);
      (l.materials || []).forEach((m) => {
        const pct = Number(m.percent || 0) / 100;
        acc[m.name] = (acc[m.name] || 0) + u * pct;
      });
    }
    return acc;
  }, [loads]);

  const silicaTotals = useMemo(() => {
    return loads.reduce(
      (acc, l) => {
        if ((l.materials || []).some((m) => isSilicaMaterialName(m.name))) {
          acc.weightKg += Number(l.silicaWeightKg || 0);
          acc.bagCount += Number(l.bagCount || 0);
          acc.amount += Number(l.amount || 0);
          acc.binCount += 1;
        }
        return acc;
      },
      { weightKg: 0, bagCount: 0, amount: 0, binCount: 0 },
    );
  }, [loads]);

  const chargeItemTotals = useMemo(() => {
    return loads.reduce((acc, l) => {
      if (l.chargeType && !isSilicaMaterialName(l.chargeType)) {
        const key = l.chargeType;
        if (!acc[key]) {
          acc[key] = {
            quantity: 0,
            unitLabel: l.chargeUnitLabel || "item",
            amount: 0,
          };
        }
        acc[key].quantity += Number(l.chargeQuantity || 0);
        acc[key].amount += Number(l.amount || 0);
      }
      return acc;
    }, {});
  }, [loads]);

  const currentTotal = useMemo(
    () => Object.values(currentBin).reduce((s, v) => s + (Number(v) || 0), 0),
    [currentBin],
  );
  const remaining = Math.max(0, 100 - currentTotal);

  const resetBuilder = () => setCurrentBin({});

  const canRecordMaterialBin = !!truckId && !!categoryId && !!binId;
  const hasSilicaInput = Number(silicaWeightKg || 0) >= 250;

  const canRecordSilicaBin =
    !!truckId && !!categoryId && !!binId && hasSilicaInput;

  const finalizeCurrentBin = (next) => {
    const materials = Object.entries(next)
      .filter(([, v]) => (Number(v) || 0) > 0)
      .map(([name, percent]) => ({ name, percent: Number(percent) }));

    if (!materials.length || !truckId || !binId || !categoryId) return;

    const record = {
      createdAt: Date.now(),
      truckId,
      truckRego: selectedTruck?.rego || selectedTruck?.name || "",
      loadType: selectedTruck?.loadType || "",
      binId,
      binName: selectedBin?.name || selectedBin?.size || "",
      binCategoryId: selectedBin?.categoryId || categoryId || "",
      binCategoryName:
        selectedBin?.categoryName || selectedCategory?.name || "",
      overload: !!overload,
      units: unitsMultiplier,
      materials,
      photoUrl: "",
    };

    setLoads((l) => [...l, record]);
    resetBuilder();
  };

  const recordSilicaBin = () => {
    if (!canRecordSilicaBin) {
      alert(
        "Please select Load Type, Category, Bin Type and choose a silica weight.",
      );
      return;
    }

    const weightKg = Number(silicaWeightKg || 0);
    const record = {
      createdAt: Date.now(),
      truckId,
      truckRego: selectedTruck?.rego || selectedTruck?.name || "",
      loadType: selectedTruck?.loadType || "",
      binId,
      binName: selectedBin?.name || selectedBin?.size || "",
      binCategoryId: selectedBin?.categoryId || categoryId || "",
      binCategoryName:
        selectedBin?.categoryName || selectedCategory?.name || "",
      overload: !!overload,
      units: unitsMultiplier,
      materials: [{ name: SILICA_MATERIAL_NAME, percent: 100 }],
      silicaWeightKg: weightKg,
      bagCount: Number(silicaBagCount || 0),
      chargeType: SILICA_MATERIAL_NAME,
      chargeQuantity: weightKg,
      chargeUnitLabel: "kg",
      unitPrice: SILICA_UNIT_PRICE,
      amount: weightKg * SILICA_UNIT_PRICE,
      photoUrl: "",
    };

    setLoads((prev) => [...prev, record]);
    setSilicaWeightKg("");
    setSilicaBagCount("");
  };

  const recordChargeItem = (item) => {
    const quantity = Number(chargeItemCounts[item.key] || 0);
    if (!canRecordMaterialBin || quantity <= 0) {
      alert("Please select Load Type, Category, Bin Type and enter a quantity.");
      return;
    }

    const record = {
      createdAt: Date.now(),
      truckId,
      truckRego: selectedTruck?.rego || selectedTruck?.name || "",
      loadType: selectedTruck?.loadType || "",
      binId,
      binName: selectedBin?.name || selectedBin?.size || "",
      binCategoryId: selectedBin?.categoryId || categoryId || "",
      binCategoryName:
        selectedBin?.categoryName || selectedCategory?.name || "",
      overload: !!overload,
      units: 0,
      materials: [{ name: item.label, percent: 100 }],
      chargeType: item.name,
      chargeQuantity: quantity,
      chargeUnitLabel: item.unitLabel,
      unitPrice: item.unitPrice,
      amount: quantity * item.unitPrice,
      photoUrl: "",
    };

    setLoads((prev) => [...prev, record]);
    setChargeItemCounts((prev) => ({ ...prev, [item.key]: "" }));
  };

  const handleClickPercent = (waste, pct) => {
    if (!canRecordMaterialBin) {
      alert("Please select Load Type, Category and Bin Type first.");
      return;
    }

    if (pct === 100) {
      finalizeCurrentBin({ [waste]: 100 });
      return;
    }

    const use = Math.min(pct, remaining);
    if (use <= 0) return;

    setCurrentBin((prev) => {
      const next = {
        ...prev,
        [waste]: Math.min(100, (prev[waste] || 0) + use),
      };
      const total = Object.values(next).reduce(
        (s, v) => s + (Number(v) || 0),
        0,
      );
      if (total >= 100) {
        setTimeout(() => finalizeCurrentBin(next), 0);
      }
      return next;
    });
  };

  function removeLoad(idx) {
    setLoads((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handlePhotoSelect(file, idx) {
    if (!file) return;
    try {
      setUploading((u) => ({ ...u, [idx]: true }));
      const l = loads[idx];
      const ext = (file.name?.split(".").pop() || "jpg").toLowerCase();
      const path = `emptyOverloadPhotos/${job?.id || "unknownJob"}/${l.createdAt}.${ext}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, file);
      const url = await getDownloadURL(ref);
      setLoads((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], photoUrl: url, photoPath: path };
        return next;
      });
    } catch (err) {
      alert("Upload failed: " + (err?.message || String(err)));
    } finally {
      setUploading((u) => ({ ...u, [idx]: false }));
    }
  }

  async function save(status) {
    const missingPhoto = loads.some((l) => l.overload && !l.photoUrl);
    if (missingPhoto) {
      alert(
        "Photo required: please upload a photo for each overloaded bin before saving.",
      );
      return false;
    }

    const patch = {
      truckId,
      binCategoryId: categoryId || "",
      binId,
      emptyLoads: loads,
      emptyLoadTotals: {
        totalUnits,
        byMaterial: totalsByMaterial,
        silica: silicaTotals,
      },
      updatedAt: serverTimestamp(),
    };

    if (status) patch.status = status;

    try {
      await upsertLoadsLedger(db, { job, companyId, companyName, loads });
      await updateDoc(doc(db, "jobs", job.id), patch);
      setHasSaved(true);
      return true;
    } catch (e) {
      console.error("save failed", e);
      return false;
    }
  }

  function exportCSV() {
    const detailHeader = [
      "Job ID",
      "Service Date",
      "Company",
      "Customer",
      "Driver Email",
      "Truck Rego",
      "Load Type",
      "Category",
      "Bin",
      "Overload",
      "Units",
      "Materials",
      "Silica Weight (kg)",
      "Silica Bags",
      "Charge Quantity",
      "Charge Unit",
      "Unit Price",
      "Amount",
      "Photo URL",
      "Created At",
    ];

    const serviceDate = job?.scheduledAt || job?.date || null;
    const companyLabel = companyName || "";
    const customerName = job?.customerName || "";
    const driverEmail = job?.driverEmail || "";

    const detailRows = loads.map((l) => {
      const mats = (l.materials || [])
        .map((m) => `${m.name} ${m.percent}%`)
        .join(" + ");
      return [
        job.id,
        fmtDate(serviceDate),
        companyLabel,
        customerName,
        driverEmail,
        l.truckRego || "",
        l.loadType || "",
        l.binCategoryName || "",
        l.binName || "",
        l.overload ? "Yes" : "No",
        Number(l.units || 0),
        mats,
        Number(l.silicaWeightKg || 0),
        Number(l.bagCount || 0),
        Number(l.chargeQuantity || 0),
        l.chargeUnitLabel || "",
        l.unitPrice ?? "",
        l.amount ?? "",
        l.photoUrl || "",
        fmtDate(l.createdAt),
      ];
    });

    const summaryHeader = ["Material", "Units"];
    const summaryRows = Object.entries(totalsByMaterial).map(
      ([name, units]) => [name, Math.round(units * 100) / 100],
    );

    const rows = [
      detailHeader,
      ...detailRows,
      [],
      ["Totals by Material (units)"],
      summaryHeader,
      ...summaryRows,
      [],
      ["Silica Summary"],
      ["Total Silica Bins", silicaTotals.binCount],
      [
        "Total Silica Weight (kg)",
        Math.round(silicaTotals.weightKg * 100) / 100,
      ],
      ["Total Silica Bags", silicaTotals.bagCount],
      ["Total Silica Amount", Math.round(silicaTotals.amount * 100) / 100],
      [],
      ["Charge Item Summary"],
      ["Item", "Quantity", "Unit", "Amount"],
      ...Object.entries(chargeItemTotals).map(([name, total]) => [
        name,
        total.quantity,
        total.unitLabel,
        Math.round(total.amount * 100) / 100,
      ]),
    ];

    const fnameSafe = (companyLabel || "company")
      .replace(/[^a-z0-9]+/gi, "_")
      .replace(/^_|_$/g, "");
    const filename = `empty_loads_${fnameSafe}_${job.id}.csv`;
    downloadCSV(filename, rows);
  }

  return (
    <div className="mt-4 rounded-2xl border border-gray-200 bg-white shadow-sm p-4 sm:p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Load Type:</span>
          <select
            className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2"
            value={truckId}
            onChange={(e) => setTruckId(e.target.value)}
          >
            <option value="">Select truck</option>
            {trucks.map((t) => (
              <option key={t.id} value={t.id}>
                {t.rego || t.name || "Truck"}
                {t.loadType ? ` • ${t.loadType}` : ""}
              </option>
            ))}
          </select>
          {!trucks.length && companyId && (
            <div className="text-xs text-amber-700 mt-1">
              No trucks found for this company.
            </div>
          )}
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">
            Bin Category:
          </span>
          <select
            className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">Select category</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name || "Category"}
              </option>
            ))}
          </select>
          {!categories.length && companyId && (
            <div className="text-xs text-amber-700 mt-1">
              No categories found for this company.
            </div>
          )}
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Bin Type:</span>
          <select
            className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2"
            value={binId}
            onChange={(e) => setBinId(e.target.value)}
            disabled={!categoryId}
          >
            <option value="">
              {categoryId ? "Select bin" : "Select category first"}
            </option>
            {binsForCategory.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name || b.size || "Bin"}
              </option>
            ))}
          </select>
          {categoryId && !binsForCategory.length && (
            <div className="text-xs text-amber-700 mt-1">
              No bins found in this category.
            </div>
          )}
        </label>
      </div>

      {selectedCategoryName && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          Selected category:{" "}
          <span className="font-semibold">{selectedCategoryName}</span>
        </div>
      )}

      {!selectedIsSilica ? (
        <>
          <div className="mt-4 rounded-xl border p-3 bg-white">
            <div className="flex items-center justify-between">
              <div className="font-medium">Current Bin Progress</div>
              <div className="text-sm text-gray-600">{currentTotal}%</div>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded mt-2 overflow-hidden">
              <div
                className="h-2 bg-emerald-600 transition-all"
                style={{ width: `${Math.min(100, currentTotal)}%` }}
              />
            </div>

            {Object.keys(currentBin).length > 0 ? (
              <div className="mt-3 text-sm text-gray-800">
                {Object.entries(currentBin).map(([w, v]) => (
                  <span
                    key={w}
                    className="inline-flex items-center px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 mr-2 mb-2"
                  >
                    {w}: {v}%
                  </span>
                ))}
                <button
                  onClick={resetBuilder}
                  className="ml-2 text-xs px-2 py-1 rounded border hover:bg-gray-50"
                >
                  Clear
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-500 mt-3">
                Tap 25/50/75% to build a bin, or press 100% on a single material
                to record 1 bin instantly.
              </p>
            )}
          </div>

          <div className="mt-6 grid grid-cols-3 gap-0 rounded-lg overflow-hidden border border-gray-300">
            {MATERIALS.map((name, i) => {
              const selectedPct = currentBin[name] || 0;
              const row = Math.floor(i / 3);
              const col = i % 3;

              const gridBorders = [
                row > 0 ? "border-t border-gray-300" : "",
                col > 0 ? "border-l border-gray-300" : "",
              ].join(" ");

              return (
                <div
                  key={name}
                  className={`p-4 sm:p-5 md:p-6 bg-white ${gridBorders}`}
                >
                  <div className="h-10 w-32 mx-auto rounded-md bg-[#18586f] text-white grid place-items-center font-medium text-center text-sm select-none shadow-sm px-2">
                    {name}
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-3 w-32 mx-auto">
                    {QUICK_STEPS.map((pct) => {
                      const willOver =
                        pct !== 100 &&
                        pct > remaining &&
                        selectedPct === 0 &&
                        remaining > 0;

                      const base =
                        pct === 100
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                          : "bg-gray-100 hover:bg-gray-200 text-gray-800";

                      const active =
                        selectedPct > 0 && pct !== 100 && pct <= selectedPct
                          ? "ring-2 ring-teal-600"
                          : "";

                      const reduced = willOver ? "opacity-60" : "";

                      return (
                        <button
                          key={pct}
                          onClick={() => handleClickPercent(name, pct)}
                          className={`h-8 rounded text-xs font-semibold ${base} ${active} ${reduced}`}
                          disabled={
                            !canRecordMaterialBin ||
                            (pct !== 100 && remaining === 0)
                          }
                          title={
                            pct === 100
                              ? "Record 1 full bin of this material"
                              : `Add ${pct}% to current bin`
                          }
                        >
                          {pct}%
                        </button>
                      );
                    })}
                  </div>

                  {selectedPct > 0 && (
                    <div className="mt-1 text-[10px] text-gray-600 text-center">
                      Selected: {selectedPct}%
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-3">
            <div className="font-medium text-gray-900">Per-item charges</div>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              {CHARGE_ITEMS.map((item) => {
                const quantity = Number(chargeItemCounts[item.key] || 0);
                const amount = quantity * item.unitPrice;

                return (
                  <div
                    key={item.key}
                    className="rounded-lg border border-gray-200 bg-white p-3"
                  >
                    <div className="text-sm font-semibold text-gray-900">
                      {item.label}
                    </div>
                    <div className="mt-3 flex flex-wrap items-end gap-2">
                      <label className="block flex-1 min-w-[9rem]">
                        <span className="text-xs font-medium text-gray-600">
                          Quantity
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                          value={chargeItemCounts[item.key]}
                          onChange={(e) =>
                            setChargeItemCounts((prev) => ({
                              ...prev,
                              [item.key]: e.target.value,
                            }))
                          }
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => recordChargeItem(item)}
                        disabled={!canRecordMaterialBin || quantity <= 0}
                        className={`rounded-lg px-3 py-2 text-sm font-medium text-white ${
                          canRecordMaterialBin && quantity > 0
                            ? "bg-emerald-600 hover:bg-emerald-700"
                            : "bg-emerald-600/50 cursor-not-allowed"
                        }`}
                      >
                        Record
                      </button>
                    </div>
                    <div className="mt-2 text-xs text-gray-600">
                      {quantity > 0
                        ? `${quantity} x ${fmtMoney(item.unitPrice)} = ${fmtMoney(amount)}`
                        : `Rate: ${fmtMoney(item.unitPrice)} per ${item.unitLabel}`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 space-y-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Silica Bin Details
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Silica category uses fixed <b>{SILICA_MATERIAL_NAME}</b>. Select
              a weight per bin from 250 kg to 600 kg. Rate:{" "}
              <b>{fmtMoney(SILICA_UNIT_PRICE)} per kg</b>.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                Weight of silica bin (kg)
              </span>
              <select
                className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2"
                value={silicaWeightKg}
                onChange={(e) => setSilicaWeightKg(e.target.value)}
              >
                <option value="">Select weight</option>
                {SILICA_WEIGHT_OPTIONS.map((weight) => (
                  <option key={weight} value={weight}>
                    {weight} kg
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                Bags count (optional)
              </span>
              <input
                type="number"
                min="0"
                step="1"
                className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2"
                placeholder="How many bags came with it"
                value={silicaBagCount}
                onChange={(e) => setSilicaBagCount(e.target.value)}
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={recordSilicaBin}
              disabled={!canRecordSilicaBin}
              className={`rounded-xl px-4 py-2 text-sm font-medium text-white transition ${
                canRecordSilicaBin
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-emerald-600/50 cursor-not-allowed"
              }`}
            >
              Record Silica Bin
            </button>

            <div className="text-sm text-gray-600">
              Waste Type:{" "}
              <span className="font-medium text-gray-900">
                {SILICA_MATERIAL_NAME}
              </span>
              {Number(silicaWeightKg || 0) > 0 ? (
                <span>
                  {" "}
                  - Charge {fmtMoney(Number(silicaWeightKg || 0) * SILICA_UNIT_PRICE)}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 flex justify-start">
        <button
          type="button"
          className={`h-10 w-32 rounded-md border-2 grid place-items-center font-medium text-center text-xs select-none ${
            overload
              ? "border-emerald-600 text-emerald-700"
              : "border-gray-400 text-gray-600"
          }`}
          onClick={() => setOverload((v) => !v)}
          title="Toggles 1.5× units for the next recorded bin"
        >
          OVERLOAD {overload ? "• ON (1.5x)" : ""}
        </button>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold">Recorded Bins</div>
            <div className="text-sm text-gray-600">
              Total Units: <b>{Math.round(totalUnits * 10) / 10}</b>
            </div>
          </div>

          {loads.length === 0 ? (
            <div className="text-sm text-gray-500">
              No bins yet. Add a bin to start.
            </div>
          ) : (
            <ul className="space-y-2">
              {loads.map((l, i) => {
                const isSilicaLoad = (l.materials || []).some((m) =>
                  isSilicaMaterialName(m.name),
                );
                const hasCharge = Number(l.amount || 0) > 0;

                return (
                  <li
                    key={i}
                    className="rounded-lg border p-2 text-sm flex flex-col gap-2"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium">
                          {l.binCategoryName ? `${l.binCategoryName} • ` : ""}
                          {l.binName || "Bin"} • {l.truckRego || "Truck"}{" "}
                          {l.loadType ? `(${l.loadType})` : ""} • Units{" "}
                          {l.units}
                          {l.overload ? (
                            <span className="ml-2 text-emerald-700">
                              (Overload)
                            </span>
                          ) : null}
                        </div>

                        {!isSilicaLoad ? (
                          <div className="text-gray-700">
                            {(l.materials || [])
                              .map((m) => `${m.name} ${m.percent}%`)
                              .join(" - ")}
                          </div>
                        ) : (
                          <div className="text-gray-700">
                            {SILICA_MATERIAL_NAME} - Weight{" "}
                            {Number(l.silicaWeightKg || 0)} kg
                            {Number(l.bagCount || 0) > 0
                              ? ` - Bags ${Number(l.bagCount || 0)}`
                              : ""}
                          </div>
                        )}
                        {hasCharge && (
                          <div className="text-gray-700">
                            Charge: {Number(l.chargeQuantity || 0)}{" "}
                            {l.chargeUnitLabel || "items"} x{" "}
                            {fmtMoney(l.unitPrice || 0)} ={" "}
                            {fmtMoney(l.amount || 0)}
                          </div>
                        )}
                      </div>

                      <button
                        className="px-2.5 py-1 rounded border text-xs self-start hover:shadow-sm hover:-translate-y-[1px] active:translate-y-0 transition"
                        onClick={() => removeLoad(i)}
                        title="Remove this bin"
                      >
                        Remove
                      </button>
                    </div>

                    {l.overload && (
                      <div className="rounded-md bg-gray-50 border p-2">
                        <div className="text-xs font-medium text-gray-800 mb-1">
                          Overload photo (required)
                        </div>
                        {l.photoUrl ? (
                          <a
                            href={l.photoUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-700 underline text-xs"
                          >
                            View uploaded photo
                          </a>
                        ) : (
                          <label className="text-xs inline-flex items-center gap-2">
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              disabled={!!uploading[i]}
                              onChange={(e) =>
                                handlePhotoSelect(e.target.files?.[0], i)
                              }
                            />
                            {uploading[i] ? (
                              <span>Uploading…</span>
                            ) : (
                              <span>Upload photo</span>
                            )}
                          </label>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="rounded-xl border p-3 space-y-4">
          <div>
            <div className="font-semibold mb-2">Totals by Material (units)</div>
            {Object.keys(totalsByMaterial).length === 0 ? (
              <div className="text-sm text-gray-500">—</div>
            ) : (
              <ul className="space-y-1 text-sm">
                {Object.entries(totalsByMaterial).map(([name, units]) => (
                  <li key={name} className="flex justify-between">
                    <span>{name}</span>
                    <span>{Math.round(units * 100) / 100}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border-t pt-3">
            <div className="font-semibold mb-2">Silica Summary</div>
            <ul className="space-y-1 text-sm">
              <li className="flex justify-between">
                <span>Total Silica Bins</span>
                <span>{silicaTotals.binCount}</span>
              </li>
              <li className="flex justify-between">
                <span>Total Silica Weight (kg)</span>
                <span>{Math.round(silicaTotals.weightKg * 100) / 100}</span>
              </li>
              <li className="flex justify-between">
                <span>Total Silica Bags</span>
                <span>{silicaTotals.bagCount}</span>
              </li>
              <li className="flex justify-between">
                <span>Total Silica Amount</span>
                <span>{fmtMoney(silicaTotals.amount)}</span>
              </li>
            </ul>
          </div>

          <div className="border-t pt-3">
            <div className="font-semibold mb-2">Charge Item Summary</div>
            {Object.keys(chargeItemTotals).length === 0 ? (
              <div className="text-sm text-gray-500">-</div>
            ) : (
              <ul className="space-y-1 text-sm">
                {Object.entries(chargeItemTotals).map(([name, total]) => (
                  <li key={name} className="flex justify-between gap-3">
                    <span>
                      {name} ({total.quantity} {total.unitLabel})
                    </span>
                    <span>{fmtMoney(total.amount)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 transition"
          onClick={async () => {
            await save("in_progress");
          }}
          title="Save your progress"
        >
          Save
        </button>

        <button
          className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition ${
            hasSaved
              ? "bg-emerald-600 hover:bg-emerald-700"
              : "bg-emerald-600/60 cursor-not-allowed"
          }`}
          disabled={!hasSaved || isCompleting}
          onClick={async () => {
            if (isCompleting) return;
            setIsCompleting(true);
            const ok = await save("completed");
            if (!ok) {
              setIsCompleting(false);
              return;
            }
            try {
              const _jobId =
                job?.id ||
                job?.jobId ||
                job?.jobID ||
                job?.uid ||
                job?._key ||
                job?.key ||
                "";
              if (_jobId) localStorage.setItem("autoFinishJobId", _jobId);
              try {
                window.dispatchEvent(new Event("autoFinishJob"));
              } catch {}
            } catch {}
            navigate("/driver", { replace: true });
          }}
          title={hasSaved ? "Complete" : "Save first to enable"}
        >
          {isCompleting ? "Completing..." : "Complete"}
        </button>

        <button
          className="rounded-lg border border-gray-900 bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-black transition disabled:opacity-60"
          onClick={exportCSV}
          disabled={!loads?.length}
          title={
            loads?.length
              ? "Download CSV of added bins"
              : "Nothing to export yet"
          }
        >
          Export CSV
        </button>
      </div>
    </div>
  );
}
